import axios from 'axios'
import { hostUI } from '../../configuraciones'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

const initialState = {
    records: [],
    pagination: {
        totalRecords: 0,
        totalPages: 1,
        currentPage: 1,
        limit: 10
    },
    loading: false,
    error: null
}

const OBTENER_MADURACION_LISTA = 'bitacoraMaduracion/obtenerLista'
const GUARDAR_MADURACION_RECORD = 'bitacoraMaduracion/guardarRecord'
const ACTUALIZAR_MADURACION_RECORD = 'bitacoraMaduracion/actualizarRecord'
const CAMBIAR_ESTATUS_MADURACION = 'bitacoraMaduracion/cambiarEstatus'
const ASIGNAR_INSPECTOR_MADURACION = 'bitacoraMaduracion/asignarInspector'
const ELIMINAR_MADURACION_RECORD = 'bitacoraMaduracion/eliminarRecord'

// GET list of maduracion records from BackendInspeccion (hostUI)
export const obtenerMaduracionRecords = createAsyncThunk(
    OBTENER_MADURACION_LISTA,
    async (params, { rejectWithValue }) => {
        try {
            let idLogin = null;
            let page = 1;
            let limit = 10;
            let search = '';

            if (typeof params === 'object' && params !== null) {
                idLogin = params.idLogin || params.id_login || null;
                page = params.page || 1;
                limit = params.limit || 10;
                search = params.search || '';
            } else {
                idLogin = params;
            }

            const queryParts = [];
            if (idLogin) queryParts.push(`idLogin=${idLogin}`);
            if (page) queryParts.push(`page=${page}`);
            if (limit) queryParts.push(`limit=${limit}`);
            if (search) queryParts.push(`search=${encodeURIComponent(search)}`);

            const queryString = queryParts.length ? `?${queryParts.join('&')}` : '';
            const url = `${hostUI}bitacora-produccion/maduracion${queryString}`;

            const res = await axios.get(url);
            return {
                records: res.data.data || [],
                pagination: res.data.pagination || { totalRecords: (res.data.data || []).length, totalPages: 1, currentPage: 1, limit: 10 }
            };
        } catch (error) {
            console.error("Error al obtener registros de maduración:", error);
            return rejectWithValue(error.response?.data || error.message);
        }
    }
)

// POST create new record
export const guardarMaduracionRecord = createAsyncThunk(
    GUARDAR_MADURACION_RECORD,
    async (recordData, { rejectWithValue }) => {
        try {
            const url = `${hostUI}bitacora-produccion/maduracion`
            const res = await axios.post(url, recordData)
            return res.data.data
        } catch (error) {
            console.error("Error al crear registro de maduración:", error)
            return rejectWithValue(error.response?.data || error.message)
        }
    }
)

// PUT update existing record
export const actualizarMaduracionRecord = createAsyncThunk(
    ACTUALIZAR_MADURACION_RECORD,
    async ({ id, recordData }, { rejectWithValue }) => {
        try {
            const url = `${hostUI}bitacora-produccion/maduracion/${id}`
            const res = await axios.put(url, recordData)
            return res.data.data
        } catch (error) {
            console.error("Error al actualizar registro de maduración:", error)
            return rejectWithValue(error.response?.data || error.message)
        }
    }
)

// PUT update status & observations
export const actualizarEstatusMaduracionRecord = createAsyncThunk(
    CAMBIAR_ESTATUS_MADURACION,
    async ({ id, estatus, observaciones, observacionesCorreccion, observaciones_correccion }, { rejectWithValue }) => {
        try {
            const url = `${hostUI}bitacora-produccion/maduracion/${id}/estatus`
            const obsCorr = observacionesCorreccion || observaciones_correccion || observaciones;
            const res = await axios.put(url, { estatus, observaciones: obsCorr, observacionesCorreccion: obsCorr, observaciones_correccion: obsCorr })
            return res.data.data
        } catch (error) {
            console.error("Error al actualizar estatus de maduración:", error)
            return rejectWithValue(error.response?.data || error.message)
        }
    }
)

// PUT assign inspector
export const asignarInspectorMaduracionRecord = createAsyncThunk(
    ASIGNAR_INSPECTOR_MADURACION,
    async ({ id, idinspector, vbo_inspector }, { rejectWithValue }) => {
        try {
            const url = `${hostUI}bitacora-produccion/maduracion/${id}/asignar-inspector`
            const res = await axios.put(url, { idinspector, vbo_inspector })
            return res.data.data
        } catch (error) {
            console.error("Error al asignar inspector a maduración:", error)
            return rejectWithValue(error.response?.data || error.message)
        }
    }
)

// DELETE record
export const eliminarMaduracionRecord = createAsyncThunk(
    ELIMINAR_MADURACION_RECORD,
    async (id, { rejectWithValue }) => {
        try {
            const url = `${hostUI}bitacora-produccion/maduracion/${id}`
            await axios.delete(url)
            return id
        } catch (error) {
            console.error("Error al eliminar registro de maduración:", error)
            return rejectWithValue(error.response?.data || error.message)
        }
    }
)

const bitacoraMaduracionSlice = createSlice({
    name: 'bitacoraMaduracion',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Obtener registros
            .addCase(obtenerMaduracionRecords.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(obtenerMaduracionRecords.fulfilled, (state, action) => {
                state.loading = false;
                state.records = action.payload.records;
                state.pagination = action.payload.pagination;
            })
            .addCase(obtenerMaduracionRecords.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Guardar registro
            .addCase(guardarMaduracionRecord.fulfilled, (state, action) => {
                if (action.payload) {
                    state.records.unshift(action.payload);
                    state.pagination.totalRecords += 1;
                }
            })
            // Actualizar registro
            .addCase(actualizarMaduracionRecord.fulfilled, (state, action) => {
                if (action.payload) {
                    const idx = state.records.findIndex(r => r.id === action.payload.id || r.id === action.payload.id_bitacora_maduracion);
                    if (idx !== -1) {
                        state.records[idx] = { ...state.records[idx], ...action.payload };
                    }
                }
            })
            // Actualizar Estatus
            .addCase(actualizarEstatusMaduracionRecord.fulfilled, (state, action) => {
                if (action.payload) {
                    const idx = state.records.findIndex(r => r.id === action.payload.id || r.id === action.payload.id_bitacora_maduracion);
                    if (idx !== -1) {
                        state.records[idx] = { ...state.records[idx], ...action.payload };
                    }
                }
            })
            // Asignar Inspector
            .addCase(asignarInspectorMaduracionRecord.fulfilled, (state, action) => {
                if (action.payload) {
                    const idx = state.records.findIndex(r => r.id === action.payload.id || r.id === action.payload.id_bitacora_maduracion);
                    if (idx !== -1) {
                        state.records[idx] = { ...state.records[idx], ...action.payload };
                    }
                }
            })
            // Eliminar registro
            .addCase(eliminarMaduracionRecord.fulfilled, (state, action) => {
                state.records = state.records.filter(r => r.id !== action.payload && r.id_bitacora_maduracion !== action.payload);
                state.pagination.totalRecords = Math.max(0, state.pagination.totalRecords - 1);
            });
    }
})

export default bitacoraMaduracionSlice.reducer;
